import React, { useState, useEffect } from 'react';
import { Typography, Button, UserTypeChangeModal } from '../../kit';
import { USER_TYPES } from './config';
import { themeColors as defaultThemeColors } from '../../theme/theme';
import { useTranslation } from '@/kit/useTranslation';

function UserTypeSelector({
  userTypeDetails,
  onUserTypeChange,
  onLogout,
  loading,
  error,
  themeColors = {},
}) {
  const tc = { ...defaultThemeColors, ...themeColors };
  const { t } = useTranslation();
  const [localType, setLocalType] = useState(userTypeDetails);
  const [toggled, setToggled] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [pendingType, setPendingType] = useState(null);

  useEffect(() => {
    setLocalType(userTypeDetails);
    setToggled(false);
  }, [userTypeDetails]);

  const handleRadioChange = (val) => {
    setLocalType(val);
    setToggled(val !== userTypeDetails);
  };

  const handleSave = async () => {
    // Show logout modal instead of directly saving
    setPendingType(localType);
    setShowLogoutModal(true);
  };

  const handleLogoutConfirm = async () => {
    // First update the user type, then logout
    try {
      await onUserTypeChange(pendingType);
      // Then trigger logout
      if (onLogout) {
        onLogout();
      }
    } catch (e) {
      console.error('Failed to update user type:', e);
    }
    setShowLogoutModal(false);
    setPendingType(null);
  };

  const handleCancel = () => {
    setLocalType(userTypeDetails);
    setToggled(false);
  };

  const handleModalClose = () => {
    setShowLogoutModal(false);
    setPendingType(null);
    setLocalType(userTypeDetails);
    setToggled(false);
  };

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          borderBottom: `1px solid ${tc.profileBorder}`,
          padding: '12px 0',
        }}
      >
        <Typography
          type="body1"
          text={t('profile.userType')}
          style={{
            flex: 1,
            color: tc.profileText,
            fontWeight: 500,
            marginTop: 6,
          }}
        />
        <div style={{ flex: 2, marginLeft: 12 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="radio"
                name="userTypeDetails"
                value={USER_TYPES.INDIVIDUAL}
                checked={localType === USER_TYPES.INDIVIDUAL}
                onChange={() => handleRadioChange(USER_TYPES.INDIVIDUAL)}
              />
              <span>{t('profile.individual')}</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="radio"
                name="userTypeDetails"
                value={USER_TYPES.BUSINESS}
                checked={localType === USER_TYPES.BUSINESS}
                onChange={() => handleRadioChange(USER_TYPES.BUSINESS)}
              />
              <span>{t('profile.business')}</span>
            </label>
          </div>
          {toggled && (
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <Button
                variant="contained"
                size="small"
                onClick={handleSave}
                disabled={loading?.userType}
              >
                {loading?.userType ? t('profile.saving') : t('common.save')}
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={handleCancel}
                disabled={loading?.userType}
              >
                {t('common.cancel')}
              </Button>
            </div>
          )}
          {error && (
            <Typography
              type="caption"
              text={error}
              style={{
                color: tc.failedColor || 'red',
                fontSize: 12,
                marginTop: 4,
              }}
            />
          )}
        </div>
      </div>

      {/* Logout confirmation modal */}
      <UserTypeChangeModal
        open={showLogoutModal}
        onClose={handleModalClose}
        onLogout={handleLogoutConfirm}
        currentType={userTypeDetails}
        newType={pendingType}
        loading={loading?.userType}
      />
    </>
  );
}

export default UserTypeSelector;
